/**
 * Registration profile fields orchestrator
 *
 * Mounts the three vanilla-TS controls that make up the shared registration
 * field block (Country + Preferred language pickers, the role card grid, and
 * the conditional social handle) and wires the one relationship between them:
 * the selected role decides whether the social handle is asked for.
 *
 * The Vite entrypoint (`app/frontend/entrypoints/registration.ts`) calls this
 * on every auth page; it is a no-op wherever the shared partial is not
 * rendered (login, password reset, …).
 */

import {
  mountSearchablePickers,
  type SearchablePickerHandle,
} from "./searchablePicker";
import { mountRoleGrid, type RoleGridHandle } from "./roleGrid";
import { mountSocialHandle, type SocialHandleHandle } from "./socialHandle";

const ROOT_SELECTOR = "[data-registration-profile-fields]";

export interface RegistrationProfileFieldsHandle {
  /** Tear down every control mounted by this call. */
  destroy: () => void;
}

/**
 * Resolve the field-block root inside a scope.
 *
 * @param scope - an element that either IS the root or contains it
 * @returns the root element, or `null` when the page has no field block
 */
function findRoot(scope: ParentNode): HTMLElement | null {
  if (scope instanceof Element && scope.matches(ROOT_SELECTOR)) {
    return scope as HTMLElement;
  }
  return scope.querySelector<HTMLElement>(ROOT_SELECTOR);
}

/**
 * Mount and wire the whole registration field block.
 *
 * @param scope - the element or document to search; defaults to `document`
 * @returns a handle, or `null` when the field block is not on this page
 */
export function mountRegistrationProfileFields(
  scope: ParentNode = document,
): RegistrationProfileFieldsHandle | null {
  const root = findRoot(scope);
  if (!root) return null;

  const pickers: SearchablePickerHandle[] = mountSearchablePickers(root);
  const socialHandle: SocialHandleHandle | null = mountSocialHandle(root);
  const roleGrid: RoleGridHandle | null = mountRoleGrid(root, {
    onChange: (roleId) => socialHandle?.setRole(roleId),
  });

  // Reconcile the conditional field with whatever the server rendered as the
  // current role (empty on a fresh form, populated when re-rendering after a
  // validation error).
  socialHandle?.setRole(roleGrid?.selectedRole() ?? null);

  return {
    destroy: () => {
      pickers.forEach((picker) => picker.destroy());
      roleGrid?.destroy();
      socialHandle?.destroy();
    },
  };
}

export { mountSearchablePicker, mountSearchablePickers } from "./searchablePicker";
export { mountRoleGrid } from "./roleGrid";
export { mountSocialHandle } from "./socialHandle";
